import Box from '@mui/material/Box'
import Typography from '@mui/material/Typography'
import KNCountryFlag from 'components/KNCountryFlag/KNCountryFlag'
import KNDetailsPopover from 'components/KNDetailsPopover/KNDetailsPopover'
import KNDetailsPopoverMap from 'components/KNDetailsPopover/KNDetailsPopoverMap'
import usePopoverState from 'components/KNDetailsPopover/usePopoverState'
import type { MapMarker } from 'components/KNMapLibre/types'
import { getRouteName } from 'global/helpers/activeRoute'
import { analyticsEvent } from 'global/helpers/analytics'
import { type ReactElement, useCallback } from 'react'
import { useTranslation } from 'react-i18next'
import { useLocation } from 'react-router-dom'

export interface LocationLinkProps {
  countryCode?: string
  city?: string
  zipCode?: string
  street?: string
  marker?: MapMarker
  lineBreaks?: 'all' | 'street' | 'city'
  hideMap?: boolean
}

const LocationLink = ({
  countryCode,
  city,
  zipCode,
  street,
  marker,
  lineBreaks,
  hideMap,
}: LocationLinkProps): ReactElement => {
  const { t } = useTranslation()
  const location = useLocation()
  const {
    anchorEl: mapAnchorEl,
    open: mapOpen,
    handleClick: handleMapClick,
    handleClose: handleMapClose,
  } = usePopoverState()

  const handleLocationClick = useCallback((event: React.MouseEvent<HTMLElement>) => {
    if (hideMap) {
      return
    }
    handleMapClick(event)
    analyticsEvent('polestar_cs_leg_location_popover', [getRouteName(location.pathname)])
  }, [hideMap])

  return (
    <>
      <Typography color="primary.main" variant="p3" component="p">
        <Box
          component="span"
          onClick={marker && handleLocationClick}
          sx={{
            cursor: marker ? 'pointer' : undefined,
          }}
        >
          {street}
          {city && (
            <>
              {street && (lineBreaks === 'all' || lineBreaks === 'street' ? <br /> : ', ')}
              {[zipCode, city].filter((value) => value).join(', ')}
            </>
          )}
          {countryCode && (
            <>
              {(street || city) && (lineBreaks === 'all' || lineBreaks === 'city' ? <br /> : ', ')}
              <KNCountryFlag countryCode={countryCode} />
              {t(`countries.${countryCode}`)}
            </>
          )}
        </Box>
      </Typography>
      {marker && (
        <KNDetailsPopover open={mapOpen} onClose={handleMapClose} anchorEl={mapAnchorEl}>
          <KNDetailsPopoverMap markers={[marker]} />
        </KNDetailsPopover>
      )}
    </>
  )
}

export default LocationLink
